<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">Maintenance & Support</h1>
  <p class="text-muted mb-0">Get help, request service, and track your maintenance activities</p>
</div>

<!-- Loading State -->
@if (isLoading()) {
  <div class="d-flex justify-content-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
}

@if (!isLoading()) {
  <!-- Contact Support Section -->
  <div class="card mb-4 border-primary">
    <div class="card-body">
      <div class="row align-items-center">
        <div class="col-lg-8">
          <div class="d-flex align-items-start gap-3">
            <div class="support-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 size-56px">
              <i class="bi bi-headset fs-4"></i>
            </div>
            <div>
              <h4 class="mb-2 fw-bold">Need Help? We're Here for You</h4>
              <p class="text-muted mb-3">Our support team is available to assist with technical issues, equipment problems, and any questions about your drone operations.</p>
              <div class="d-flex flex-wrap gap-4">
                <div>
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <i class="bi bi-telephone-fill text-primary"></i>
                    <a [href]="'tel:' + technicalSupportPhone" class="fw-semibold fs-5 text-decoration-none">
                      {{ formattedSupportPhone }}
                    </a>
                  </div>
                </div>
                <div>
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <i class="bi bi-clock-fill text-primary"></i>
                    <span class="fw-medium">Mon-Fri: 7am - 7pm CT</span>
                  </div>
                  <small class="text-muted">Sat: 8am - 4pm CT</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 mt-3 mt-lg-0">
          <div class="d-grid gap-2">
            <button class="btn btn-primary" routerLink="/maintenance/support-tickets/new">
              <i class="bi bi-plus-lg me-2"></i>Open Support Ticket
            </button>
            <button class="btn btn-outline-primary" routerLink="/maintenance/service-appointments/new">
              <i class="bi bi-calendar-plus me-2"></i>Request Service Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ADN Fix in the Field Videos -->
  <div class="card mb-4">
    <div class="card-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-2">
          <div class="rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center size-40px">
            <i class="bi bi-play-circle text-danger"></i>
          </div>
          <h5 class="mb-0 fw-bold">ADN Fix in the Field Videos</h5>
        </div>
        <a routerLink="/maintenance/training-videos" class="btn btn-sm btn-link text-decoration-none p-0">
          View All <i class="bi bi-arrow-right ms-1"></i>
        </a>
      </div>
      <div class="ratio ratio-16x9">
        <iframe
          [src]="playlistEmbedUrl"
          title="ADN Fix in the Field Videos"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          loading="lazy"
          class="rounded"
        ></iframe>
      </div>
    </div>
  </div>

  <!-- What's the Difference Section -->
  <div class="row g-4 mb-4">
    <div class="col-md-6">
      <div class="card h-100">
        <div class="card-body">
          <div class="d-flex align-items-center gap-2 mb-3">
            <div class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center size-40px">
              <i class="bi bi-ticket-perforated text-primary"></i>
            </div>
            <h5 class="mb-0 fw-bold">Support Tickets</h5>
          </div>
          <p class="text-muted mb-3">For questions, issues, or problems that can be resolved remotely. Our support team will respond via email and work with you to find a solution.</p>
          <ul class="list-unstyled mb-3 small">
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Technical questions & troubleshooting</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Software or firmware issues</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Account & billing inquiries</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Feature requests & feedback</li>
          </ul>
          <a routerLink="/maintenance/support-tickets" class="btn btn-sm btn-outline-primary">
            View All Tickets <i class="bi bi-arrow-right ms-1"></i>
          </a>
        </div>
      </div>
    </div>
    <div class="col-md-6">
      <div class="card h-100">
        <div class="card-body">
          <div class="d-flex align-items-center gap-2 mb-3">
            <div class="rounded-circle bg-info-subtle d-flex align-items-center justify-content-center size-40px">
              <i class="bi bi-calendar-check text-info"></i>
            </div>
            <h5 class="mb-0 fw-bold">Service Appointments</h5>
          </div>
          <p class="text-muted mb-3">For hands-on maintenance, repairs, or inspections that require a technician. We'll schedule a time to service your drone.</p>
          <ul class="list-unstyled mb-3 small">
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Physical repairs & replacements</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Preventive maintenance & inspections</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Battery service & calibration</li>
            <li class="mb-2"><i class="bi bi-check-circle text-success me-2"></i>Annual drone checkups</li>
          </ul>
          <a routerLink="/maintenance/service-appointments" class="btn btn-sm btn-outline-info">
            View All Appointments <i class="bi bi-arrow-right ms-1"></i>
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- Stats & Open Items Row -->
  <div class="row g-4 mb-4">
    <!-- Open Tickets -->
    <div class="col-lg-6">
      <div class="card h-100">
        <div class="card-header d-flex justify-content-between align-items-center py-3">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-ticket-perforated text-primary"></i>
            <h6 class="mb-0 fw-bold">Open Support Tickets</h6>
            @if (openTicketCount() > 0) {
              <span class="badge bg-primary">{{ openTicketCount() }}</span>
            }
          </div>
          <a routerLink="/maintenance/support-tickets" class="btn btn-sm btn-link text-decoration-none p-0">
            View All
          </a>
        </div>
        <div class="card-body p-0">
          @if (openTickets().length > 0) {
            <div class="list-group list-group-flush">
              @for (ticket of openTickets(); track ticket.id) {
                <a [routerLink]="['/maintenance/support-tickets', ticket.id]" class="list-group-item list-group-item-action py-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="me-3">
                      <h6 class="mb-1 fw-medium">{{ truncateText(ticket.subject, 45) }}</h6>
                      <small class="text-muted">
                        @if (ticket.ticketNumber) {
                          #{{ ticket.ticketNumber }} &middot;
                        }
                        {{ getRelativeTime(ticket.createdAt) }}
                      </small>
                    </div>
                    <span class="badge" [ngClass]="getTicketStatusBadgeClass(ticket.status)">
                      {{ getTicketStatusLabel(ticket.status) }}
                    </span>
                  </div>
                </a>
              }
            </div>
          } @else if (proFeaturesLocked()) {
            <div class="text-center text-muted py-4">
              <i class="bi bi-lock-fill fs-1 d-block mb-2 opacity-50"></i>
              <p class="mb-0">Support Tickets are a Pro feature</p>
              <a routerLink="/billing/upgrade" class="small">Upgrade to Pro</a>
            </div>
          } @else {
            <div class="text-center text-muted py-4">
              <i class="bi bi-check-circle fs-1 d-block mb-2 opacity-50"></i>
              <p class="mb-0">No open tickets</p>
              <small>All caught up!</small>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Pending Appointments -->
    <div class="col-lg-6">
      <div class="card h-100">
        <div class="card-header d-flex justify-content-between align-items-center py-3">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-calendar-check text-info"></i>
            <h6 class="mb-0 fw-bold">Active Service Appointments</h6>
            @if (pendingAppointmentCount() > 0) {
              <span class="badge bg-info">{{ pendingAppointmentCount() }}</span>
            }
          </div>
          <a routerLink="/maintenance/service-appointments" class="btn btn-sm btn-link text-decoration-none p-0">
            View All
          </a>
        </div>
        <div class="card-body p-0">
          @if (activeAppointments().length > 0) {
            <div class="list-group list-group-flush">
              @for (appt of activeAppointments(); track appt.id) {
                <a [routerLink]="['/maintenance/service-appointments', appt.id]" class="list-group-item list-group-item-action py-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="me-3">
                      <h6 class="mb-1 fw-medium">{{ appt.droneName || 'Drone Service' }}</h6>
                      <small class="text-muted">
                        @if (appt.scheduledDate) {
                          Scheduled: {{ formatDate(appt.scheduledDate) }}
                        } @else {
                          Requested: {{ formatDate(appt.preferredDate) }}
                        }
                      </small>
                    </div>
                    <span class="badge" [ngClass]="getAppointmentStatusBadgeClass(appt.status)">
                      {{ getAppointmentStatusLabel(appt.status) }}
                    </span>
                  </div>
                </a>
              }
            </div>
          } @else if (proFeaturesLocked()) {
            <div class="text-center text-muted py-4">
              <i class="bi bi-lock-fill fs-1 d-block mb-2 opacity-50"></i>
              <p class="mb-0">Service Appointments are a Pro feature</p>
              <a routerLink="/billing/upgrade" class="small">Upgrade to Pro</a>
            </div>
          } @else {
            <div class="text-center text-muted py-4">
              <i class="bi bi-calendar-check fs-1 d-block mb-2 opacity-50"></i>
              <p class="mb-0">No active appointments</p>
              <small>Your drones are all set!</small>
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Coming Soon: Knowledge Base & AI Chat -->
  <div class="row g-4 mb-4">
    <div class="col-md-6">
      <div class="card h-100 bg-light border-dashed">
        <div class="card-body text-center py-4">
          <div class="rounded-circle bg-secondary-subtle d-inline-flex align-items-center justify-content-center mb-3 size-56px">
            <i class="bi bi-book text-secondary fs-4"></i>
          </div>
          <h5 class="fw-bold text-secondary">Knowledge Base</h5>
          <p class="text-muted mb-0 small">Coming soon: Search our library of guides, tutorials, and troubleshooting articles to find answers quickly.</p>
        </div>
      </div>
    </div>
    <div class="col-md-6">
      <div class="card h-100 bg-light border-dashed">
        <div class="card-body text-center py-4">
          <div class="rounded-circle bg-secondary-subtle d-inline-flex align-items-center justify-content-center mb-3 size-56px">
            <i class="bi bi-chat-dots text-secondary fs-4"></i>
          </div>
          <h5 class="fw-bold text-secondary">AI Support Assistant</h5>
          <p class="text-muted mb-0 small">Coming soon: Chat with our AI assistant for instant answers to common questions and guided troubleshooting.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Recent Activity -->
  <div class="card">
    <div class="card-header py-3">
      <h6 class="mb-0 fw-bold">Recent Maintenance Activity</h6>
    </div>
    <div class="card-body p-0">
      <div class="activity-list">
        @for (activity of activities(); track activity.id) {
          <div
            class="activity-item"
            [class.activity-item-clickable]="hasResourceLink(activity)"
            (click)="hasResourceLink(activity) && navigateToResource(activity)"
            [attr.role]="hasResourceLink(activity) ? 'button' : null"
          >
            <div class="activity-icon">
              <i [class]="getActivityIconClass(activity)"></i>
            </div>
            <div class="activity-content">
              <p class="activity-message mb-0 fw-medium">{{ activity.message }}</p>
              <small class="text-muted">{{ getRelativeTime(activity.createdAt) }}</small>
            </div>
            @if (hasResourceLink(activity)) {
              <div class="activity-link-indicator">
                <i class="bi bi-chevron-right text-muted"></i>
              </div>
            }
          </div>
        }

        @if (activities().length === 0) {
          <div class="text-center text-muted py-4">
            <i class="bi bi-activity fs-1 d-block mb-2 opacity-50"></i>
            <p class="mb-0">No recent maintenance activity</p>
          </div>
        }
      </div>
    </div>
  </div>
}
